import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Banner from 'components/shared/banner';
import Footer from 'components/shared/footer';
import Header from 'components/shared/header';
import MobileMenu from 'components/shared/mobile-menu';
import SEO from 'components/shared/seo';
import MainContext from 'context/main';
import useParamsDetectionUrl from 'hooks/use-params-detection-url';

import LangSelect from '../lang-select';

const Layout = (props) => {
  const { seo, children, pageContext, headerTheme, withoutHeader, withoutBanner, footerTheme } =
    props;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const hreflangs =
    pageContext?.translations?.length > 0
      ? {
          [pageContext.language.locale]: pageContext.pageUrl,
          ...pageContext.translations.reduce((acc, translation) => {
            acc[translation.language.locale] = translation.uri;

            return acc;
          }, {}),
        }
      : null;
  const enhancedSeo = {
    lang: pageContext?.language?.locale,
    hreflangs,
    ...seo,
  };

  useParamsDetectionUrl();

  const handleHeaderBurgerClick = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  return (
    <MainContext.Provider value={pageContext}>
      {enhancedSeo && <SEO {...enhancedSeo} />}
      {!withoutBanner && <Banner />}
      <div className="relative flex min-h-screen flex-col">
        {!withoutHeader && (
          <Header
            theme={headerTheme}
            isMobileMenuOpen={isMobileMenuOpen}
            onBurgerClick={handleHeaderBurgerClick}
          />
        )}
        <main className="grow">{children}</main>
        <Footer theme={footerTheme} />
        <MobileMenu isOpen={isMobileMenuOpen} />
        {pageContext?.translations?.length > 0 && (
          <LangSelect
            className="!fixed z-50 bottom-12 right-12 2xl:bottom-8 2xl:right-8 xl:hidden"
            openTo="top"
          />
        )}
      </div>
    </MainContext.Provider>
  );
};

Layout.propTypes = {
  seo: PropTypes.shape({
    metaDesc: PropTypes.string,
    metaKeywords: PropTypes.string,
    metaRobotsNoindex: PropTypes.string,
    opengraphDescription: PropTypes.string,
    opengraphTitle: PropTypes.string,
    opengraphImage: PropTypes.objectOf(PropTypes.any),
    opengraphUrl: PropTypes.string,
    title: PropTypes.string,
    schema: PropTypes.shape({
      type: PropTypes.string.isRequired,
      datePublished: PropTypes.string.isRequired,
      dateModified: PropTypes.string.isRequired,
    }),
  }),
  children: PropTypes.node.isRequired,
  pageContext: PropTypes.objectOf(PropTypes.any).isRequired,
  headerTheme: PropTypes.oneOf(['white', 'gray']).isRequired,
  withoutHeader: PropTypes.bool,
  withoutBanner: PropTypes.bool,
  footerTheme: PropTypes.oneOf(['white', 'gray']),
};

Layout.defaultProps = {
  seo: null,
  withoutHeader: false,
  withoutBanner: false,
  footerTheme: 'white',
};

export const query = graphql`
  fragment wpPageSeo on WpPage {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: JPG, width: 1200, height: 630)
          }
        }
      }
    }
  }

  fragment wpPostSeo on WpPost {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: JPG, width: 1200, height: 630)
          }
        }
      }
    }
  }

  fragment wpEventSeo on WpEvent {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(width: 1200, height: 630)
          }
        }
      }
    }
  }

  fragment wpTutorialSeo on WpTutorial {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(width: 1200, height: 630)
          }
        }
      }
    }
  }

  fragment wpCustomerSeo on WpCustomer {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: JPG, width: 1200, height: 630)
          }
        }
      }
    }
  }

  fragment wpPostAuthorsSeo on WpPostAuthors {
    seo {
      title
      metaDesc
      metaKeywords
      metaRobotsNoindex
      opengraphTitle
      opengraphDescription
      opengraphUrl
      opengraphImage: twitterImage {
        localFile {
          childImageSharp {
            gatsbyImageData(formats: JPG, width: 1200, height: 630)
          }
        }
      }
    }
  }
`;

export default Layout;
